import { useCallback } from "react";
import { useParams } from "react-router";

import { useMutation } from "@apollo/client";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useLensSubmissionRoutesV3 } from "../../core/routes/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { getRoles } from "../../shared/api/queries";
import { type ApolloResult } from "../../shared/models";
import { type CreateLensSpaceMutation, type CreateLensSpaceMutationVariables, createLensSpace } from "../api/mutation";
import { getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation } from "../api/queries";

export const useCreateLensSpace = (
    organizationId: string,
    lensSpaceName: string,
): ApolloResult<{
    createLensSpace: () => Promise<void>;
}> => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE]>();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const [createLensSpaceAction, { loading, error }] = useMutation<
        CreateLensSpaceMutation,
        CreateLensSpaceMutationVariables
    >(createLensSpace, {
        refetchQueries: isMyLensesOrgsV2Enabled
            ? [{ query: getRoles }]
            : [
                  { query: getRoles },
                  {
                      query: getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation,
                      variables: { submissionId },
                  },
              ],
    });
    const { goToLensFolderSelect } = useLensSubmissionRoutesV3();

    const performCreateLensSpaceAction = useCallback(async () => {
        if (!organizationId || !lensSpaceName || loading) {
            return;
        }

        try {
            await createLensSpaceAction({ variables: { organizationId, lensSpaceName } });

            goToLensFolderSelect(submissionId);
        } catch {
            // use error from Create Lens Space Mutation if failed
        }
    }, [createLensSpaceAction, loading, submissionId, organizationId, lensSpaceName, goToLensFolderSelect]);

    return { createLensSpace: performCreateLensSpaceAction, loading, error };
};
